/* ==========================================================================
   Modern Cooking — Mobile nav controller (v7)  ·  Round 3 (2026-07-08)

   Below-header layout (round 3): the drawer sits under the sticky site
   header. On open (and on resize / orientationchange while open), we
   measure the section-header's bottom edge and set it as the CSS
   `--mnav-top` var on `.menu-drawer--v7`, so the drawer runs from that
   line to the bottom of the viewport. The announcement bar scrolls
   away, so we recompute rather than hardcode.

   Dawn's <header-drawer> web component (assets/global.js) still owns
   drawer open/close via <details>: the header's hamburger/X toggle is
   the ONLY open/close control.

   Interactive bindings owned here:
     data-mobile-nav-open="key"    → opens the matching sub-panel
     data-mobile-nav-back          → returns to root
     data-mobile-nav-open-sheet    → opens the region/language sheet
     data-mobile-nav-close-sheet   → closes it

   ROUND 2 FIX (retained) — Dawn's blanket button handler
   ----------------------------------------------------------------------
   assets/global.js:437-439 attaches an `onCloseButtonClick` handler to
   every <button> inside the drawer (except a couple of localization
   exceptions). Round-2 bug: my root-item buttons had Dawn's close
   handler attached to them. Fix: attach my click handlers directly in
   the CAPTURE phase and call stopImmediatePropagation, so Dawn's
   later-attached bubble listener never fires.

   ROUND 3 (2026-07-08) — dropped from round 2:
     · data-mobile-nav-close handler (internal close button removed;
       header X is the only close control)
     · full-viewport overlay contract (replaced by below-header layout)
   ========================================================================== */
(function () {
  'use strict';

  function initMobileNav(root) {
    if (!root || root.__mobileNavInit) return;
    root.__mobileNavInit = true;

    var details = root.closest('details');
    var drawer = root.closest('.menu-drawer--v7');
    var track = root.querySelector('[data-mobile-nav-track]');
    if (!track) return;

    var panels = Array.prototype.slice.call(
      root.querySelectorAll('[data-mobile-nav-panel]')
    );
    var openPanel = null;

    /* A6 (2026-07-28) — offscreen sub-panels are only translated off-
       screen with `transform: translateX(100%)` in component-mobile-nav.css.
       Their links and buttons stay in the tab order and get read by
       screen readers. `aria-hidden` alone is not enough to remove
       elements from focus. Set `inert` on every non-root panel at
       mount so the initial off-screen state is focus-safe; openSub /
       markClosed keep it in sync as the user drills in. Native `inert`
       is supported everywhere the theme targets (Safari 15.4+, Chrome
       102+, Firefox 112+). Audit finding F7 / batch A #A6. */
    for (var pi = 0; pi < panels.length; pi++) {
      if (!panels[pi].classList.contains('mobile-nav__panel--root')) {
        panels[pi].setAttribute('inert', '');
      }
    }

    /* -----------------------------------------------------------------
       Below-header layout: measure the sticky header's bottom edge and
       set it as --mnav-top on the drawer. Recomputed on open, resize,
       and orientationchange while open — the announcement bar scrolls
       away so the header's bottom moves.
       ----------------------------------------------------------------- */
    var headerEl = document.querySelector('.section-header') ||
                   document.querySelector('header.header-wrapper') ||
                   document.querySelector('#shopify-section-header');

    function setDrawerTop() {
      if (!drawer) return;
      var top = 0;
      if (headerEl) {
        var rect = headerEl.getBoundingClientRect();
        // `rect.bottom` is header's bottom-edge in viewport coords;
        // clamp non-negative (if the header has scrolled up further
        // than its own height, treat as 0).
        top = Math.max(0, rect.bottom);
      }
      drawer.style.setProperty('--mnav-top', top + 'px');
    }

    function bindMeasureListeners() {
      window.addEventListener('resize', setDrawerTop, { passive: true });
      window.addEventListener('orientationchange', setDrawerTop, { passive: true });
      window.addEventListener('scroll', setDrawerTop, { passive: true });
    }

    /* -----------------------------------------------------------------
       Sub-panel drill-down
       ----------------------------------------------------------------- */
    function openSub(key) {
      var target = null;
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].getAttribute('data-mobile-nav-panel') === key) {
          target = panels[i];
          break;
        }
      }
      if (!target) return;
      if (openPanel && openPanel !== target) markClosed(openPanel);
      target.classList.add('is-open');
      target.setAttribute('aria-hidden', 'false');
      // A6 — panel is now on-screen and interactive; must be reachable
      // by keyboard + AT. Must clear inert BEFORE the .focus() call
      // below because focus on an inert subtree is a no-op.
      target.removeAttribute('inert');
      target.scrollTop = 0;
      track.classList.add('is-sub-open');
      openPanel = target;
      var back = target.querySelector('[data-mobile-nav-back]');
      if (back && typeof back.focus === 'function') {
        setTimeout(function () { back.focus(); }, 50);
      }
      var trigger = root.querySelector(
        '[data-mobile-nav-open="' + key + '"]'
      );
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    function markClosed(p) {
      if (!p) return;
      p.classList.remove('is-open');
      p.setAttribute('aria-hidden', 'true');
      // A6 — panel is off-screen again; take it out of the tab order and
      // hide from AT so screen-reader users don't traverse hidden content.
      p.setAttribute('inert', '');
      var key = p.getAttribute('data-mobile-nav-panel');
      var trigger = root.querySelector(
        '[data-mobile-nav-open="' + key + '"]'
      );
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    function closeSub() {
      if (openPanel) {
        var key = openPanel.getAttribute('data-mobile-nav-panel');
        markClosed(openPanel);
        openPanel = null;
        var trigger = root.querySelector(
          '[data-mobile-nav-open="' + key + '"]'
        );
        if (trigger && typeof trigger.focus === 'function') {
          try { trigger.focus(); } catch (e) {}
        }
      }
      track.classList.remove('is-sub-open');
    }

    /* -----------------------------------------------------------------
       Bottom sheet: region + language
       ----------------------------------------------------------------- */
    function openSheet() {
      root.classList.add('is-sheet-open');
      var trigger = root.querySelector('[data-mobile-nav-open-sheet]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      var close = root.querySelector('[data-mobile-nav-close-sheet]');
      if (close && typeof close.focus === 'function') {
        setTimeout(function () { close.focus(); }, 50);
      }
    }
    function closeSheet() {
      root.classList.remove('is-sheet-open');
      var trigger = root.querySelector('[data-mobile-nav-open-sheet]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        try { trigger.focus(); } catch (e) {}
      }
    }
    function sheetIsOpen() {
      return root.classList.contains('is-sheet-open');
    }

    /* -----------------------------------------------------------------
       Capture-phase wiring — beats Dawn's blanket button handler
       (see round-2 note at top of file).
       ----------------------------------------------------------------- */
    function attachCapture(sel, handler) {
      Array.prototype.forEach.call(
        root.querySelectorAll(sel),
        function (el) {
          el.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            handler(el, e);
          }, true);
        }
      );
    }

    attachCapture('[data-mobile-nav-open]', function (el) {
      openSub(el.getAttribute('data-mobile-nav-open'));
    });
    attachCapture('[data-mobile-nav-back]', function () {
      closeSub();
    });
    attachCapture('[data-mobile-nav-open-sheet]', function () {
      openSheet();
    });
    // A7 (2026-07-28) — bind close on both the real close <button>
    // (`[data-mobile-nav-close-sheet]`) AND the scrim <div>
    // (`[data-mobile-nav-close-sheet-scrim]`). openSheet's
    // querySelector at :164 uses only the primary attribute so
    // focus lands on the <button>, not the div.
    attachCapture('[data-mobile-nav-close-sheet], [data-mobile-nav-close-sheet-scrim]', function () {
      closeSheet();
    });

    /* -----------------------------------------------------------------
       Escape order: sheet → panel → drawer. Dawn's <header-drawer>
       keyup handler owns the drawer-close step; we return without
       stopping propagation so Dawn still sees Esc when neither sheet
       nor panel is open.
       ----------------------------------------------------------------- */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (!details || !details.hasAttribute('open')) return;
      if (sheetIsOpen()) {
        e.stopPropagation();
        closeSheet();
      } else if (openPanel) {
        e.stopPropagation();
        closeSub();
      }
    });

    /* -----------------------------------------------------------------
       body.mc-nav-open toggle + close-time state reset + measure

       Observes <details> for `open` attribute changes. On open:
         · re-measure header bottom → --mnav-top
         · add body.mc-nav-open (scroll-lock + widget suppression)
       On close:
         · remove body.mc-nav-open
         · reset drill-down + sheet state so next open starts at root
       ----------------------------------------------------------------- */
    if (details) {
      var onOpenStateChanged = function () {
        var isOpen = details.hasAttribute('open');
        document.body.classList.toggle('mc-nav-open', isOpen);
        if (isOpen) {
          setDrawerTop();
        } else {
          closeSheet();
          closeSub();
        }
      };
      var mo = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName === 'open') {
            onOpenStateChanged();
            return;
          }
        }
      });
      mo.observe(details, { attributes: true, attributeFilter: ['open'] });
      onOpenStateChanged();
    }

    // Bind measurement listeners once. setDrawerTop is cheap
    // (getBoundingClientRect + one style write) and no-ops when the
    // drawer is closed; safe on every scroll/resize/rotation event.
    bindMeasureListeners();
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-mobile-nav]');
    for (var i = 0; i < nodes.length; i++) initMobileNav(nodes[i]);
  }

  /* ==========================================================================
     Round-6 (2026-07-08): baseline-align the Google Merchant badge with
     the Klaviyo help-hub launcher (see the corner-widget block in
     assets/style-guide-overrides.css for the rule).

     Google injects the badge with `style="… bottom: 46px !important; …"`
     as an *inline !important* style. CSS `!important` can't override
     inline `!important`; only `HTMLElement.style.setProperty(prop,
     value, 'important')` can. Watch for the badge to appear (it's
     injected after page load by Google's async script) and rewrite
     the `bottom` inline value to match the site's corner-widget
     baseline.
     ========================================================================== */
  function alignGoogleBadge() {
    var badge = document.querySelector('#google-merchantwidget-iframe-wrapper');
    if (!badge) return false;
    var target = (window.innerWidth <= 749) ? '16px' : '24px';
    var current = badge.style.getPropertyValue('bottom');
    if (current === target && badge.style.getPropertyPriority('bottom') === 'important') return true;
    badge.style.setProperty('bottom', target, 'important');
    return true;
  }
  function watchGoogleBadge() {
    // Try immediately; if the badge isn't there yet, poll for ~10s and
    // then bail (the widget is loaded async by Google's SDK).
    if (alignGoogleBadge()) return;
    var tries = 0;
    var t = setInterval(function () {
      if (alignGoogleBadge() || ++tries > 40) clearInterval(t);
    }, 250);
    // Also re-apply on resize (mobile → desktop breakpoint crossing).
    window.addEventListener('resize', alignGoogleBadge, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); watchGoogleBadge(); });
  } else {
    boot();
    watchGoogleBadge();
  }
})();
